import moment from 'moment'

export function useUtil() {
    const calcuateHours = (startDate, endDate) => {
        const diff = ( endDate.getTime() - startDate.getTime() ) / 1000
        return Math.abs( diff / ( 60 * 60 ) )
    }
    // (Optional) non-breaking alias; uncomment if you want a correctly spelled helper without breaking callers.
    // const calculateHours = calcuateHours

    const getDatesArray = (startDate, endDate) => {
        const dates = []
        const start = moment(startDate)
        const end = moment(endDate)
        while ( end.diff(start,'days') >= 0 ) {
            dates.push( start.format('M/D/YYYY') ) // <-- fixed YYYY
            start.add( 1, 'day' )
        }
        return dates
    }

    const getDatesInLastQuarterToDate = () => {
        const end = moment()
        const start = moment().add(-1, 'quarter').startOf('quarter')
        return getDatesArray( start, end )
    }

    const parseJwt = (token) => {
        return JSON.parse(window.atob(token.split('.')[1]));
    }

    // NEW: ISO week helpers (moment uses ISO week via isoWeek / isoWeekYear)
    const getIsoWeek = (dateLike, useUtc = true) => {
        const m = useUtc ? moment.utc(dateLike) : moment(dateLike)
        return m.isoWeek()
    }

    const getIsoYearWeek = (dateLike, useUtc = true) => {
        const m = useUtc ? moment.utc(dateLike) : moment(dateLike)
        // e.g. "2025-W43"
        return `${m.isoWeekYear()}-W${String(m.isoWeek()).padStart(2, '0')}`
    }

    // UPDATED: moment-based formatter
    const formatDate = (dateInput, includeTime = true, utcTime = true, includeDayOfWeek = false) => {
        if (!dateInput) return 'N/A'

        // moment handles ISO strings (with/without 'Z'), Date objects, and timestamps
        let m = utcTime ? moment.utc(dateInput) : moment(dateInput)
        if (!m.isValid()) return 'Invalid Date'

        let fmt = includeDayOfWeek ? 'ddd M/D/YYYY' : 'M/D/YYYY'
        if (includeTime) {
            // Note: without moment-timezone, 'z' isn't reliable. We annotate UTC manually when utcTime=true.
            fmt += utcTime ? ' HH:mm:ss [UTC]' : ' h:mm:ss A'
        }
        return m.format(fmt)
    }

    const formatLocalDateTime = (dateInput) => {
        if (!dateInput) return 'N/A'
        const m = moment(dateInput)
        if (!m.isValid()) return 'Invalid Date'
        return m.format('M/D/YYYY h:mm A')
    }

    const formatMetricValue = (value, metricName) => {
        const currencyMetrics = ['Gross profit', 'Sales', 'Total net sales', 'Payroll', 'Payroll cost', 'Adjusted payroll cost', 'Adjusted daily payroll cost'];
        const percentMetrics = ['Adjusted payroll percent of sales']
        const dateFields = ['Check Date']
        const stringFields = ['Store', 'Employee']

        if( value === 0 ){
            return "-"
        }
        
        if( stringFields.includes(metricName) )
        {
            return value
        }

        if( dateFields.includes(metricName))
        {
            return formatDate( value, false, false )
        }

        let style = 'decimal'

        if (currencyMetrics.includes(metricName)) {
            style = 'currency';
        }

        if( percentMetrics.includes(metricName) )
        {
            style="percent";
            value = value/100;
        }
    
        const defaultOptions = {
            style: style,
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        };

        return new Intl.NumberFormat('en-US', defaultOptions).format(value);
    }

    const formatRates = (value, metricName) => {
        const currencyRates = ['Hourly', 'Weekly', 'Annual Salary'];
        const percentRates = ['Commission']

        if( value === 0 ){
            return "-"
        }

        let style = 'decimal'
        if (currencyRates.includes(metricName)) style = 'currency';
        if (percentRates.includes(metricName)) style = 'percent';
    
        const defaultOptions = {
            style,
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        };

        return new Intl.NumberFormat('en-US', defaultOptions).format(value);
    }

    const formatDatePickerDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const accountTypes = [
      { value: 0, title: 'Asset' },
      { value: 1, title: 'Liability' },
      { value: 2, title: 'Equity' },
      { value: 3, title: 'Revenue' },
      { value: 4, title: 'Expense' },
    ]

    const accountTypeMap = accountTypes.reduce((map, type) => {
        map[type.value] = type.title;
        return map;
    }, {});

    const types = [
        { value: 0, title: "Sale" },
        { value: 1, title: "Tax" },
        { value: 2, title: "Fee" },
        { value: 3, title: "Payment" },
        { value: 4, title: "Advance" },
        { value: 5, title: "Receivable" }
    ]

    const typeMap = types.reduce((map, type) => {
        map[type.value] = type.title;
        return map;
    }, {});

    const aggregationTypes = [
        { value: 0, title: 'Sum' },
        { value: 1, title: 'Percentage' },
        { value: 2, title: 'Average' },
        { value: 3, title: 'Max' },
        { value: 4, title: 'Min' },
        { value: 5, title: 'Count' },
    ]

    const aggregationTypeMap = aggregationTypes.reduce((map, type) => {
        map[type.value] = type.title;
        return map;
    }, {});

    const goalGroupTypes = [
        { value: 1, title: 'Month' },
        { value: 2, title: 'Quarter' },
        { value: 3, title: 'Year' }
    ]

    const goalGroupTypeMap = goalGroupTypes.reduce((map, type) => {
        map[type.value] = type.title
        return map
    }, {})

    const positionPayGroupAggregationStrategies = [
        { value: 0, title: 'All', separator: '|' },
        { value: 1, title: 'Max', separator: '>' },
        { value: 3, title: 'Diff', separator: '<>' },
        { value: 2, title: 'Min', separator: '<' }
    ]
        

    const positionPayGroupAggregationStrategyMap = positionPayGroupAggregationStrategies.reduce((map, type) => {
        map[type.value] = type
        return map
    }, {})

    const getPayGroupAggregationStrategy = (strategyValue) => {
        return positionPayGroupAggregationStrategyMap[strategyValue] || positionPayGroupAggregationStrategies[0]
    }

    const formatPayGroupPayTypes = (payGroup) => {
        if (!payGroup || !payGroup.groupPayTypes || payGroup.groupPayTypes.length === 0) {
            return []
        }

        const strategy = getPayGroupAggregationStrategy(payGroup.aggregationStrategy)
        const sortedPayTypes = payGroup.groupPayTypes
            .sort((a, b) => (a.priority || 0) - (b.priority || 0))

        const result = []
        sortedPayTypes.forEach((gpt, index) => {
            result.push({
                type: 'text',
                value: gpt.payType?.name || 'Unknown'
            })

            if (index < sortedPayTypes.length - 1) {
                result.push({
                    type: 'separator',
                    value: strategy.separator
                })
            }
        })

        return result
    }

    function isEmpty(val) {
        if (val == null) return true;
        if (Array.isArray(val)) return val.length === 0;
        if (typeof val === 'object') return Object.keys(val).length === 0;
        if (typeof val === 'string') return val.trim().length === 0;
        return false;
    }

    function camelToKebab(str) {
        return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    }

    function truncateText(text, maxLength) {
        if (!text) return '';
        return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    }

    return {
        // date/number helpers
        formatRates,
        calcuateHours,
        // calculateHours, // optional alias
        getDatesArray,
        getDatesInLastQuarterToDate,
        parseJwt,
        formatDate,
        formatLocalDateTime,
        formatDatePickerDate,
        formatMetricValue,
        getIsoWeek,
        getIsoYearWeek,

        // maps + enums
        types,
        typeMap,
        aggregationTypes,
        aggregationTypeMap,
        goalGroupTypes,
        goalGroupTypeMap,
        accountTypes,
        accountTypeMap,

        // misc
        isEmpty,
        camelToKebab,
        truncateText,

        // pay group display
        positionPayGroupAggregationStrategies,
        positionPayGroupAggregationStrategyMap,
        getPayGroupAggregationStrategy,
        formatPayGroupPayTypes
    }
}
